import type { WorkflowStep } from '../services/workflowService';
import {
  isEditStepType,
  isReviewPhaseStepType,
  isSignatureStepType,
  WorkflowActionCodes,
  WORKFLOW_STATE_TRANSITION_ACTIONS,
} from './workflowConstants';

export interface IncoherentWorkflowAction {
  action: string;
  reason: string;
}

/** Ações que fazem handoff para a etapa Signature (edit/review → signature). */
const HANDOFF_TO_SIGNATURE_ACTIONS = new Set(
  [
    WorkflowActionCodes.Approve,
    WorkflowActionCodes.SendForSignature
  ].map((a) => a.toLowerCase())
);

function workflowHasReviewPhase(steps: WorkflowStep[]): boolean {
  return steps.some(s => isReviewPhaseStepType(s.type));
}

function workflowHasSignaturePhase(steps: WorkflowStep[]): boolean {
  return steps.some(s => isSignatureStepType(s.type));
}

export function isHandoffToSignatureAction(action: string): boolean {
  return HANDOFF_TO_SIGNATURE_ACTIONS.has(action.trim().toLowerCase());
}

/** Handoff partindo de Edit (não inclui Approve da Review). */
function isEditHandoffToSignatureAction(action: string): boolean {
  const a = action.trim().toLowerCase();
  return (a === WorkflowActionCodes.SendForSignature.toLowerCase());
}

/** True quando a ação é de handoff e a próxima etapa do template é Signature. */
export function actionAdvancesToSignatureStep(
  action: string,
  nextStepType: string | null | undefined
): boolean {
  return isHandoffToSignatureAction(action) && isSignatureStepType(nextStepType);
}

export function getIncoherentActionsForStep(
  step: WorkflowStep,
  stepIndex: number,
  orderedSteps: WorkflowStep[]
): IncoherentWorkflowAction[] {
  const actions = step.allowedActions ?? [];
  if (actions.length === 0) {
    return [];
  }

  const nextStep = stepIndex + 1 < orderedSteps.length ? orderedSteps[stepIndex + 1] : null;
  const incoherent: IncoherentWorkflowAction[] = [];

  for (const action of actions) {
    if (!action?.trim()) {
      continue;
    }

    if (!WORKFLOW_STATE_TRANSITION_ACTIONS.some(a => a.toLowerCase() === action.trim().toLowerCase())) {
      continue;
    }

    const reason = getDefinitionIncoherenceReason(step, nextStep, orderedSteps, action.trim());
    if (reason) {
      incoherent.push({ action: action.trim(), reason });
    }
  }

  return incoherent;
}

function getDefinitionIncoherenceReason(
  step: WorkflowStep,
  nextStep: WorkflowStep | null,
  orderedSteps: WorkflowStep[],
  action: string
): string | null {
  if (isEditStepType(step.type)) {
    if (action.toLowerCase() === 'sendforreview' && !workflowHasReviewPhase(orderedSteps)) {
      return 'Este fluxo não possui etapa de revisão.';
    }

    if (
      action.toLowerCase() === 'publish' &&
      (workflowHasReviewPhase(orderedSteps) || workflowHasSignaturePhase(orderedSteps))
    ) {
      return 'Publicação direta só é permitida quando o fluxo não possui etapa de revisão nem de assinatura.';
    }

    if (isEditHandoffToSignatureAction(action)) {
      if (workflowHasReviewPhase(orderedSteps)) {
        return 'Solicitar ou enviar para assinatura a partir de edição só é permitido quando o fluxo não possui revisão.';
      }

      if (!nextStep || !isSignatureStepType(nextStep.type)) {
        return `A ação '${action}' requer próxima etapa do tipo assinatura (Signature).`;
      }
    }

    if (action.toLowerCase() === 'completeedit' && (!nextStep || !isEditStepType(nextStep.type))) {
      return 'Avanço entre etapas de edição só é permitido quando a próxima etapa também é edição.';
    }
  }

  if (
    isReviewPhaseStepType(step.type) &&
    action.toLowerCase() === 'sendforsignature' &&
    (!nextStep || !isSignatureStepType(nextStep.type))
  ) {
    return 'Enviar para assinatura só é permitido quando a próxima etapa do fluxo for assinatura.';
  }

  return null;
}

export function getIncoherenceReasonForStepAction(
  steps: WorkflowStep[],
  stepId: string,
  action: string
): string | null {
  const ordered = [...steps].sort((a, b) => {
    if (a.order !== b.order) {
      return a.order - b.order;
    }
    return (a.id || '').localeCompare(b.id || '');
  });
  const stepIndex = ordered.findIndex(s => s.id === stepId);
  if (stepIndex < 0) {
    return null;
  }

  const incoherent = getIncoherentActionsForStep(ordered[stepIndex], stepIndex, ordered);
  const match = incoherent.find(i => i.action.toLowerCase() === action.trim().toLowerCase());
  return match?.reason ?? null;
}

/**
 * Avalia se uma ação seria incoerente para a etapa no contexto atual do workflow,
 * independentemente de a ação estar ou não em `allowedActions`.
 * Use este método para filtrar ações na UI antes de oferecê-las ao usuário.
 */
export function wouldActionBeIncoherentForStep(
  steps: WorkflowStep[],
  stepId: string,
  action: string
): string | null {
  const ordered = [...steps].sort((a, b) => {
    if (a.order !== b.order) {
      return a.order - b.order;
    }
    return (a.id || '').localeCompare(b.id || '');
  });
  const stepIndex = ordered.findIndex(s => s.id === stepId);
  if (stepIndex < 0) {
    return null;
  }

  const trimmed = action.trim();
  if (!WORKFLOW_STATE_TRANSITION_ACTIONS.some(a => a.toLowerCase() === trimmed.toLowerCase())) {
    return null;
  }

  const step = ordered[stepIndex];
  const nextStep = stepIndex + 1 < ordered.length ? ordered[stepIndex + 1] : null;
  return getDefinitionIncoherenceReason(step, nextStep, ordered, trimmed);
}

export function getNextStepAfterAction(
  steps: WorkflowStep[],
  currentStepId: string,
  action: string
): WorkflowStep | null {
  const ordered = [...steps].sort((a, b) => {
    if (a.order !== b.order) {
      return a.order - b.order;
    }
    return (a.id || '').localeCompare(b.id || '');
  });
  const currentIndex = ordered.findIndex(s => s.id === currentStepId);
  if (currentIndex < 0 || currentIndex + 1 >= ordered.length) {
    return null;
  }
  return ordered[currentIndex + 1];
}
